import { useEffect, useRef, useState } from "react";
import { X, SendHorizonal } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { AtlasRecommendationForm } from "./AtlasRecommendationForm";
import { NLQChatbot } from "./NLQChatbot";
import { usePostNlqChat } from "@/services/recommendation";
import type {
  BeaconTimelineEntry,
  ManuallyDateRangeResponse,
  RecommendationResponse,
} from "@/types/recommendation";

interface BeaconWindowProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  /** Mirrors FeatureFlags:NLQChatEnabled from appsettings. Defaults to true. */
  isNlqEnabled?: boolean;
  /** Called when the user clicks "Create Soft Hold" on an Atlas result option. */
  onCreateSoftHold?: (item: RecommendationResponse) => void;
}

interface AtlasResultCardProps {
  item: RecommendationResponse;
  onCreateSoftHold?: (item: RecommendationResponse) => void;
}

function AtlasResultCard({ item, onCreateSoftHold }: AtlasResultCardProps) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow">
      {/* Header: option label left, Create Soft Hold button pinned right */}
      <div className="flex flex-nowrap items-center gap-2 mb-2 min-h-[32px]">
        <p
          className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-900"
          title={item.optionLabel}
        >
          {item.optionLabel}
        </p>
        {onCreateSoftHold && (
          <button
            type="button"
            onClick={() => onCreateSoftHold(item)}
            className="flex-shrink-0 whitespace-nowrap rounded-none border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Create Soft Hold
          </button>
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-2 text-left font-semibold text-slate-700">Date</th>
              <th className="p-2 text-left font-semibold text-slate-700">Available</th>
            </tr>
          </thead>
          <tbody>
            {item.recommendationsByDateRangeResponses.map((row, idx) => (
              <tr key={`${row.day}-${idx}`} className="border-b border-slate-100 last:border-b-0 hover:bg-blue-50 transition-colors">
                <td className="p-2 text-slate-600">{format(new Date(row.day), "MM/dd/yyyy")}</td>
                <td className="p-2 font-medium text-slate-900">{row.availableToSell}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BeaconWindow({ isOpen, onClose, triggerRef, isNlqEnabled = true, onCreateSoftHold }: BeaconWindowProps) {
  const [isFormMode, setIsFormMode] = useState(!isNlqEnabled);
  const [timeline, setTimeline] = useState<BeaconTimelineEntry[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatGuid, setChatGuid] = useState<string | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const timelineEndRef = useRef<HTMLDivElement>(null);

  const nlqMutation = usePostNlqChat();

  useEffect(() => {
    if (!isOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      messageInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof timelineEndRef.current?.scrollIntoView === "function") {
      timelineEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [timeline]);

  useEffect(() => {
    if (!isOpen) {
      triggerRef?.current?.focus();
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  async function handleSend() {
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    try {
      const response = await nlqMutation.mutateAsync({
        newMessage: trimmed,
        isNewChat: !chatGuid,
        chatGuid,
        contextId: "Recommendations",
      });

      if (response.chatGuid) {
        setChatGuid(response.chatGuid);
      }

      const aiResponse = response.aiRecommendationResponse;
      if (aiResponse) {
        setTimeline((prev) => [
          ...prev,
          {
            kind: "nlq",
            timestamp: new Date().toISOString(),
            response: aiResponse,
          },
        ]);
      }

      setNewMessage("");
      if (messageInputRef.current) {
        messageInputRef.current.style.height = "auto";
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to send message.";
      toast.error(message);
    }
  }

  function handlePreparePrompt(prompt: string) {
    setNewMessage(prompt);
    setIsFormMode(false);
    // focus textarea after React re-renders
    setTimeout(() => {
      const el = messageInputRef.current;
      if (el) {
        el.focus();
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
        // place cursor at end
        el.selectionStart = el.selectionEnd = el.value.length;
      }
    }, 0);
  }

  function handleManualResult(result: ManuallyDateRangeResponse) {
    setTimeline((prev) => [
      ...prev,
      {
        kind: "atlas",
        timestamp: new Date().toISOString(),
        recommendations: result.recommendationsDateRanges,
        displaySummary: result.displaySummary,
        hasPreferredDate: result.hasPreferredDate,
      },
    ]);
  }

  function clearChat() {
    setTimeline([]);
    setChatGuid(null);
    setNewMessage("");
    // When NLQ is disabled, stay in form mode after clearing
    setIsFormMode(!isNlqEnabled);
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} role="presentation" />
      <section
        role="dialog"
        aria-label="Atlas"
        aria-modal="true"
        className="fixed right-0 top-[6%] bottom-4 z-50 flex flex-col rounded-lg border border-slate-200 bg-white shadow-2xl w-[30vw] min-w-[320px] max-w-[500px] max-h-[calc(100vh-6%-2rem)]"
      >
        <header className="flex items-center justify-between bg-black px-4 py-3 text-white rounded-t-lg">
          <h2 className="text-base font-semibold tracking-tight">Atlas AI</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Atlas"
            className="rounded-lg p-1.5 text-white hover:bg-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Shared timeline — always visible for both NLQ and form results */}
        <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-slate-50 to-white min-h-0">
          {timeline.length === 0 && (
            <section className="mb-4 rounded-lg bg-white border border-blue-100 p-4 text-sm text-slate-700 shadow-sm">
              <p className="font-semibold text-slate-900 mb-2">👋 Welcome to Atlas</p>
              <p className="leading-relaxed">
                Get intelligent recommendations for date ranges and availability. Describe what you're looking for, or use Manual Entry below.
              </p>
            </section>
          )}
          <div className="space-y-3">
            {timeline.map((entry, idx) =>
              entry.kind === "nlq" ? (
                <NLQChatbot key={`nlq-${idx}`} response={entry.response} />
              ) : (
                <div key={`atlas-${idx}`} className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Manual Entry Result</p>
                  {entry.recommendations.map((item, cardIdx) => (
                    <AtlasResultCard key={`${item.optionLabel}-${cardIdx}`} item={item} onCreateSoftHold={onCreateSoftHold} />
                  ))}
                </div>
              )
            )}
          </div>
          <div ref={timelineEndRef} />
        </div>

        {/* Form panel — collapsible, sits above the footer without replacing the timeline */}
        {isFormMode && (
          <div className="shrink-0 border-t border-slate-200 bg-white max-h-[50%] overflow-y-auto">
            <AtlasRecommendationForm
              isNlqChatEnabled={isNlqEnabled}
              onSwitchToNlq={() => setIsFormMode(false)}
              onResult={handleManualResult}
              onPreparePrompt={isNlqEnabled ? handlePreparePrompt : undefined}
            />
          </div>
        )}

        {/* Footer — NLQ input is always available regardless of mode */}
        <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 rounded-b-lg">
          <div className="mb-3 flex items-center justify-between gap-2">
            {/* Toggle only shown when NLQ is enabled — in form-only mode the button is hidden */}
            {isNlqEnabled ? (
              <button
                type="button"
                onClick={() => setIsFormMode((prev) => !prev)}
                aria-label={isFormMode ? "Close manual entry form" : "Enter dates manually"}
                className={`text-xs font-medium transition-colors ${
                  isFormMode
                    ? "text-slate-600 hover:text-slate-800"
                    : "text-blue-600 hover:text-blue-700"
                } hover:underline`}
              >
                {isFormMode ? "✕ Close Form" : "📋 Manual Entry"}
              </button>
            ) : (
              <span className="text-xs text-slate-400">Manual Entry</span>
            )}
            <button
              type="button"
              onClick={clearChat}
              aria-label="Clear chat"
              className="text-xs font-medium text-slate-600 hover:text-slate-700 hover:underline transition-colors"
            >
              🔄 Clear
            </button>
          </div>
          {!isFormMode && (
            <div className="flex items-end gap-2 bg-slate-100 rounded-xl p-2.5 border border-slate-200 shadow-inner">
              <label htmlFor="beacon-chat-input" className="sr-only">
                Ask Atlas
              </label>
              <textarea
                id="beacon-chat-input"
                ref={messageInputRef}
                value={newMessage}
                rows={1}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder="Ask Atlas anything… (Shift+Enter for new line)"
                className="flex-1 min-h-[36px] max-h-[120px] resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm leading-snug outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 transition-all overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={!newMessage.trim() || nlqMutation.isPending}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-black text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <SendHorizonal className="h-4 w-4" />
              </button>
            </div>
          )}
        </footer>
      </section>
    </>
  );
}
